import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash-es';
import { isLocalStorageAvailable } from '../util/isLocalStorageAvailable';

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

function isUpdater<T>(value: T | ((prev: T) => T)): value is (prev: T) => T {
  return typeof value === 'function';
}

function readPersisted<T>(persistedKey: string): T | null {
  if (!isLocalStorageAvailable()) return null;

  const raw = window.localStorage.getItem(persistedKey);
  if (raw === null) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// Registry of subscribers per storage key, notified on write so other mounted
// readers of the same key stay in sync. Hand-rolled since we're on React 17
// replace with useSyncExternalStore once we're on 18+.
const readersByKey = new Map<string, Set<() => void>>();

const subscribe = (persistedKey: string, reader: () => void) => {
  const readers = readersByKey.get(persistedKey) ?? new Set();
  readers.add(reader);
  readersByKey.set(persistedKey, readers);

  return () => {
    readers.delete(reader);
    if (readers.size === 0) readersByKey.delete(persistedKey);
  };
};

const notifyReaders = (persistedKey: string) =>
  readersByKey.get(persistedKey)?.forEach((read) => read());

function usePersistedState<T>(persistedKey: string, defaultValue: T) {
  const persistedValue = useMemo(() => readPersisted<T>(persistedKey), [
    persistedKey,
  ]);

  const [state, setState] = useState<T>(
    persistedValue === null ? defaultValue : persistedValue
  );

  // Whether a value has been set for this key — either persisted from a previous
  // visit (checked at mount) or set during this session. Lets callers tell "no
  // choice yet" apart from an explicit choice, even when localStorage is
  // unavailable (a session set still flips this, it just won't persist).
  const [isSet, setIsSet] = useState(persistedValue !== null);

  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  // Take up whatever is stored under the key now. Runs when another reader
  // writes, and when the key itself changes — a different key is a different
  // value, and DE1's suggestion keys gain their DOI after the fields mount.
  useEffect(() => {
    const readStoredValue = () => {
      const stored = readPersisted<T>(persistedKey);
      const next = stored === null ? defaultValueRef.current : stored;

      setState((current) => (isEqual(current, next) ? current : next));
      setIsSet(stored !== null);
    };

    readStoredValue();

    return subscribe(persistedKey, readStoredValue);
  }, [persistedKey]);

  const setValue = useCallback<SetValue<T>>(
    (value) => {
      const persist = isLocalStorageAvailable();
      setIsSet(true);

      if (isUpdater(value)) {
        if (persist) {
          const raw = readPersisted<T>(persistedKey);
          const prev = raw === null ? defaultValueRef.current : raw;
          const next = value(prev);
          window.localStorage.setItem(persistedKey, JSON.stringify(next));
          setState(next);
          notifyReaders(persistedKey);
        } else {
          setState((current) => value(current));
        }
        return;
      }

      if (persist) {
        window.localStorage.setItem(persistedKey, JSON.stringify(value));
        setState(value);
        notifyReaders(persistedKey);
        return;
      }

      setState(value);
    },
    [persistedKey]
  );

  return [state, setValue, isSet] as const;
}

export default usePersistedState;
